import React, { useState, useRef } from "react";
import { GoogleMap, LoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { v4 as uuidv4 } from "uuid";
import Location from "../Modals/Location";
import { useTranslation } from "react-i18next";
import { HiMapPin } from "react-icons/hi2";

const MapForm = ({
  GoogleMapsAPI,
  passTemplateId,
  onAddLocation,
}: {
  GoogleMapsAPI: string;
  passTemplateId: string;
  onAddLocation: (location: Location) => void;
}) => {
  const { t } = useTranslation();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [errors, setErrors] = useState<{ name?: string; location?: string }>({});

  const [location, setLocation] = useState<Location>({
    id: uuidv4(),
    name: "",
    description: "",
    longitude: "",
    latitude: "",
    autoNotification: "",
    passTemplateId,
  });

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMarkerPosition({ lat, lng });
      setLocation(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
      setErrors(prev => ({ ...prev, location: undefined }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLocation(prev => ({ ...prev, [name]: value }));
    if (name === "name" && value.trim()) setErrors(prev => ({ ...prev, name: undefined }));
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry) {
      const lat = place.geometry.location?.lat();
      const lng = place.geometry.location?.lng();
      const name = place.name || "";
      if (lat && lng) {
        setMarkerPosition({ lat, lng });
        if (map) map.panTo({ lat, lng });
        setLocation(prev => ({ ...prev, name, latitude: lat.toString(), longitude: lng.toString() }));
        setErrors({});
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { name?: string; location?: string } = {};
    if (!location.name.trim()) newErrors.name = t("name") + " " + t("required");
    if (!location.latitude || !location.longitude) newErrors.location = t("Please select a location on the map");
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    onAddLocation(location);
    setLocation({ id: uuidv4(), name: "", description: "", longitude: "", latitude: "", autoNotification: "", passTemplateId });
    setMarkerPosition(null);
    setErrors({});
  };

  const inputClass = (hasError?: string) =>
    `w-full px-4 py-2.5 rounded-xl border text-sm text-gray-900 bg-gray-50 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition ${
      hasError ? "border-red-400 bg-red-50" : "border-gray-200"
    }`;

  return (
    <LoadScript googleMapsApiKey={GoogleMapsAPI} libraries={["places"]}>
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t("name")} <span className="text-red-500">*</span>
          </label>
          <Autocomplete
            onLoad={ac => (autocompleteRef.current = ac)}
            onPlaceChanged={handlePlaceSelect}
          >
            <input
              type="text"
              name="name"
              value={location.name}
              onChange={handleInputChange}
              placeholder={t("Search with store name in Google Maps")}
              className={inputClass(errors.name)}
            />
          </Autocomplete>
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>

        {/* Map */}
        <div className="space-y-1">
          <label className="block text-sm font-semibold text-gray-700">
            {t("location")} <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-400">{t("Click on the map to pin the location")}</p>
          <div className={`rounded-xl overflow-hidden border-2 transition ${errors.location ? "border-red-400" : "border-gray-200"}`}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "260px" }}
              center={markerPosition || { lat: 24.7136, lng: 46.6753 }}
              zoom={markerPosition ? 15 : 5}
              onClick={handleMapClick}
              onLoad={setMap}
            >
              {markerPosition && <Marker position={markerPosition} />}
            </GoogleMap>
          </div>
          {errors.location
            ? <p className="text-xs text-red-500 flex items-center gap-1"><HiMapPin className="w-3 h-3" />{errors.location}</p>
            : markerPosition && <p className="text-xs text-emerald-600 flex items-center gap-1"><HiMapPin className="w-3 h-3" />Location pinned</p>
          }
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("Description of the location")}</label>
          <textarea
            name="description"
            rows={2}
            value={location.description}
            onChange={handleInputChange}
            placeholder={t("Write the address here")}
            className={`${inputClass()} resize-none`}
          />
        </div>

        {/* Auto Notification */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("Add the Auto Notification")}</label>
          <textarea
            name="autoNotification"
            rows={2}
            value={location.autoNotification}
            onChange={handleInputChange}
            placeholder={t("Add the notification users get when they are 200 meters close to the location")}
            className={`${inputClass()} resize-none`}
          />
        </div>

        <button
          type="submit"
          className="w-full py-3 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 transition active:scale-95"
        >
          {t("Add Location")}
        </button>
      </form>
    </LoadScript>
  );
};

export default MapForm;
